import { useEffect, useRef, useState } from "react";
import { Check, Pencil, X } from "lucide-react";
import { fmtDateTime as fmtDateTimeTz } from "../lib/tz";

function fmtDisplay(iso, tz) {
  if (!iso) return "—";
  try {
    return fmtDateTimeTz(iso, tz, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      ...(tz ? { timeZoneName: "short" } : {}),
    });
  } catch { return "—"; }
}

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

/**
 * Inline editable timestamp cell. Click → datetime-local input → Enter / ✓ saves.
 * Esc / ✕ cancels. Empty value clears the timestamp.
 *
 * Props:
 *   value: ISO string | null
 *   canEdit: boolean (gating)
 *   onSave: (newIsoOrNull) => Promise<void>
 *   testId?: string
 *   placeholder?: string (label shown when empty + editable)
 */
export function EditableTimestampCell({ value, canEdit, onSave, testId, placeholder = "Set time", tz, currentReason }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toLocalInput(value));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(toLocalInput(value)); }, [value]);
  useEffect(() => { if (editing) { setReason(""); setTimeout(() => inputRef.current?.focus(), 30); } }, [editing]);

  if (!canEdit) {
    return <span data-testid={testId} className="font-mono text-xs">{fmtDisplay(value, tz)}</span>;
  }

  const commit = async () => {
    setSaving(true);
    try {
      await onSave(fromLocalInput(draft), reason.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(toLocalInput(value));
    setReason("");
    setEditing(false);
  };

  if (!editing) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={() => setEditing(true)}
        className="group inline-flex items-center gap-1.5 font-mono text-xs text-left hover:text-[#FF3B30] transition-colors"
        title={currentReason ? `Last edit reason: ${currentReason}` : "Click to edit"}
      >
        <span>{value ? fmtDisplay(value, tz) : <span className="text-zinc-500 italic">{placeholder}</span>}</span>
        <Pencil className="size-3 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }

  return (
    <div className="inline-flex flex-col gap-1" data-testid={testId ? `${testId}-editing` : undefined}>
      <div className="inline-flex items-center gap-1">
        <input
          ref={inputRef}
          type="datetime-local"
          data-testid={testId ? `${testId}-input` : undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); commit(); }
            if (e.key === "Escape") { e.preventDefault(); cancel(); }
          }}
          disabled={saving}
          className="bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] outline-none px-2 py-1 text-xs font-mono"
        />
        <button
          type="button"
          data-testid={testId ? `${testId}-save` : undefined}
          onClick={commit}
          disabled={saving}
          className="border border-[#27272A] hover:border-[#34C759] hover:text-[#34C759] text-zinc-300 p-1 transition-colors"
          title="Save"
        >
          <Check className="size-3" />
        </button>
        <button
          type="button"
          data-testid={testId ? `${testId}-cancel` : undefined}
          onClick={cancel}
          disabled={saving}
          className="border border-[#27272A] hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-300 p-1 transition-colors"
          title="Cancel"
        >
          <X className="size-3" />
        </button>
      </div>
      <input
        type="text"
        data-testid={testId ? `${testId}-reason` : undefined}
        value={reason}
        onChange={(e) => setReason(e.target.value.slice(0, 500))}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); cancel(); }
        }}
        placeholder="Reason for adjustment (optional)"
        maxLength={500}
        disabled={saving}
        className="bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] outline-none px-2 py-1 text-[11px] italic text-zinc-300 w-[220px]"
      />
    </div>
  );
}

export default EditableTimestampCell;
